// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Services facade - provides access to all domain services.
 *
 * This module handles:
 * - API client initialization
 * - Service instantiation and caching
 * - React hook for accessing services
 */

import type { ApiClient } from '@umd-dit-ai-workspace/api-client'
import { NodeApiClient } from '../api/node-api-client'

// Import services from their domain subsystems
import { LayoutService } from '@/subsystems/layout'
import { SchemaCatalogService } from '@/subsystems/schema-catalog'
import { PipelineService } from '@/subsystems/pipelines'
import { NodeInstanceService, NodeConfigService } from '@/subsystems/palette'
import { createWorkspaceTransferService, type WorkspaceTransferService } from '@/subsystems/workspace-transfer'

// Re-export NodeApiClient for backward compatibility
export { NodeApiClient } from '../api/node-api-client'

let nodeClient: NodeApiClient | null = null

/**
 * Initialize the API client. Call once at app startup.
 */
export function initializeApiClient(client: ApiClient): void {
  nodeClient = new NodeApiClient(client.getAxiosInstance())
}

/**
 * Get the node API client for making API calls.
 * @throws Error if client not initialized
 */
export function getApiClient(): NodeApiClient {
  if (!nodeClient) {
    throw new Error('API client not initialized. Call initializeApiClient() before use.')
  }
  return nodeClient
}

/**
 * Services facade - provides access to all services through a single object.
 */
export interface Services {
  readonly layout: LayoutService
  readonly schemaCatalog: SchemaCatalogService
  readonly pipeline: PipelineService
  readonly nodeInstance: NodeInstanceService
  readonly nodeConfig: NodeConfigService
  readonly workspaceTransfer: WorkspaceTransferService
  getClient(): NodeApiClient
}

let servicesInstance: Services | null = null

/**
 * Get all services through a single facade object.
 */
export function getServices(): Services {
  if (!nodeClient) {
    throw new Error('API client not initialized. Call initializeApiClient() before use.')
  }

  if (!servicesInstance) {
    const client = nodeClient
    servicesInstance = {
      layout: new LayoutService(client),
      schemaCatalog: new SchemaCatalogService(client),
      pipeline: new PipelineService(client),
      nodeInstance: new NodeInstanceService(client),
      nodeConfig: new NodeConfigService(client),
      workspaceTransfer: createWorkspaceTransferService(client),
      getClient: () => client,
    }
  }

  return servicesInstance
}

/**
 * React hook for accessing services.
 */
export function useServices(): Services {
  return getServices()
}
